/**
 * The 76 px group header: a 40 px title row over the 36 px column-header row
 * (spec M1-B2 §8.1, measured on `[ui:library/table-01-top.png]`, y 245-284 / 285-320).
 *
 * The title is Poppins 18 px / 24 px, letter-spacing −0.1 px, **painted in the group colour**;
 * the counters (`86 Items` / `9 Subitems`) are 15 px secondary text. The colour bar does not
 * run through the title row — it starts at the column-header row and continues down every
 * body row (measured: `#ff642e` at x 391-396 is absent at y 265 and present at y 302).
 *
 * A **collapsed** group is a 60 px strip instead: the title over its item count, plus the
 * columns' summary cells drawn to the full grid width with their vertical rules, then 14 px of
 * air before the next group (parity card P-030, which answers Q146). The cells are the footer's
 * — `SummaryCell` — so the two can never drift.
 */
import type { ColumnId, GroupId } from '@elizmonday/shared';
import { Icon, icons } from '@elizmonday/ui';
import { useEffect, useState } from 'react';
import type { BoardColumn, BoardGroupView } from '../data/types';
import { ColumnHeader } from './ColumnHeader';
import {
  CHECKBOX_GUTTER,
  GROUP_BAR_WIDTH,
  GROUP_COLLAPSED_STRIP_HEIGHT,
  GROUP_TITLE_ROW_HEIGHT,
  NAME_COLUMN_MIN_WIDTH,
  NAME_STICKY_CHROME_WIDTH,
} from './layout';
import { GROUP_DRAG_TYPE } from './row-drag';
import { SummaryCell } from './SummaryCell';
import { GROUP_RENAME_EVENT } from './menus/GroupMenu';

export interface GroupHeaderProps {
  group: BoardGroupView;
  top: number;
  height: number;
  collapsed: boolean;
  pinned: readonly BoardColumn[];
  scrolling: readonly BoardColumn[];
  layout: readonly { id: string; left: number; width: number }[];
  columnRange: { start: number; end: number };
  nameWidth: number;
  stickyWidth: number;
  scrollWidth: number;
  allSelected: boolean;
  sortedBy: Map<string, 'asc' | 'desc'>;
  filteredColumnIds: ReadonlySet<string>;
  /** Both forms: the counter says `1 item` and `7 items` as monday's header does. */
  itemTerminology: string;
  itemTerminologyPlural: string;
  onToggleCollapsed(): void;
  onRenameGroup(title: string): void;
  onSelectAll(selected: boolean): void;
  onOpenGroupMenu(x: number, y: number): void;
  onRenameColumn(columnId: ColumnId, title: string): void;
  onResizeColumn(columnId: ColumnId, width: number): void;
  onOpenColumnMenu(columnId: ColumnId, x: number, y: number): void;
  onMoveColumn(columnId: ColumnId, beforeColumnId: ColumnId | null): void;
  /** P-292: the header's own sort widget; `null` is its ✕. */
  onSortColumn(columnId: ColumnId, direction: 'asc' | 'desc' | null): void;
  /**
   * **P-102.** A group dropped on this header: `movedGroupId` is the group the user picked
   * up, `edge` the half of this header the pointer was in. monday drags a group by its whole
   * title row (`div.generic-group-header-name.can-drag`, `[B2§5]`).
   */
  onDropGroup?(movedGroupId: GroupId, edge: 'above' | 'below'): void;
  onAddColumn(x: number, y: number): void;
  /** P-030: a collapsed group's summary cells open the same menu the footer's do. */
  onOpenSummaryMenu?(columnId: ColumnId, x: number, y: number): void;
  /** The floating copy omits the counters and the title editor `[B2§8.1]`. */
  floating?: boolean;
  /** A row is hovering this header: paint the drop line under its title row. */
  dropEdge?: boolean;
}

export function GroupHeader({
  group,
  top,
  height,
  collapsed,
  pinned,
  scrolling,
  layout,
  columnRange,
  nameWidth,
  stickyWidth,
  scrollWidth,
  allSelected,
  sortedBy,
  filteredColumnIds,
  itemTerminology,
  itemTerminologyPlural,
  onToggleCollapsed,
  onRenameGroup,
  onSelectAll,
  onOpenGroupMenu,
  onRenameColumn,
  onResizeColumn,
  onOpenColumnMenu,
  onMoveColumn,
  onSortColumn,
  onAddColumn,
  onDropGroup,
  onOpenSummaryMenu,
  floating = false,
  dropEdge = false,
}: GroupHeaderProps) {
  const [editingTitle, setEditingTitle] = useState(false);
  /** P-102: which half of this header a dragged **group** is over — the 2 px line `[B2§5]`. */
  const [groupDropEdge, setGroupDropEdge] = useState<'above' | 'below' | null>(null);

  /**
   * "Rename group" in the group menu opens **this** editor rather than a dialog of its own —
   * the menu has no input, and the caret belongs where the title is (`[B2§8.2]`).
   */
  useEffect(() => {
    const onRequest = (event: Event) => {
      const detail = (event as CustomEvent<{ groupId: string }>).detail;
      if (detail?.groupId === String(group.id) && !floating) setEditingTitle(true);
    };
    window.addEventListener(GROUP_RENAME_EVENT, onRequest);
    return () => window.removeEventListener(GROUP_RENAME_EVENT, onRequest);
  }, [group.id, floating]);
  const nameColumn = pinned.find((column) => column.id === 'name');
  const extraPinned = pinned.filter((column) => column.id !== 'name');
  /**
   * P-101: the grid drops **before** a column, so a drop on a header's right half means
   * "before the one that follows it" — and `null`, "nothing follows it", means last. The
   * order is the one the reader sees: the name column, the other pinned ones, then the
   * scrolling ones.
   */
  const visibleOrder = [...(nameColumn ? [nameColumn] : []), ...extraPinned, ...scrolling];
  const nextColumnIdOf = (columnId: ColumnId): ColumnId | null => {
    const index = visibleOrder.findIndex((column) => column.id === columnId);
    return index < 0 ? null : (visibleOrder[index + 1]?.id ?? null);
  };

  return (
    <div
      className="ez-group-header"
      /*
       * Presentational: its two children are the rows. The rowgroup above then sees only
       * `row` children, which is what `aria-required-children` asks for (A2).
       */
      role="presentation"
      data-testid={floating ? 'group-floating-header' : 'group-header'}
      data-group={group.id}
      data-collapsed={collapsed || undefined}
      data-floating={floating || undefined}
      // A row dropped on a group header lands at the top of that group (`Grid` delegates).
      data-drop-row={floating ? undefined : 'group-header'}
      data-drop-edge={dropEdge ? 'below' : undefined}
      style={{ top, height, width: stickyWidth + scrollWidth }}
    >
      <div
        className="ez-group-header__title-row"
        role="row"
        /*
         * **P-102.** monday's drag handle for a group is the whole title row
         * (`div.generic-group-header-name.can-drag`, `[B2§5]`); the drop indicator is a 2 px
         * line. The gesture is the HTML5 drag API the rows and the column headers already
         * use — the grid is virtualised, so a library that owns the list would have to be
         * told about rows that are not in the DOM — with `text/ez-group` as its payload so a
         * row drag and a group drag can never be mistaken for each other.
         */
        draggable={!floating && !editingTitle}
        data-drop-edge={groupDropEdge ?? undefined}
        onDragStart={(event) => {
          event.dataTransfer.setData(GROUP_DRAG_TYPE, String(group.id));
          event.dataTransfer.effectAllowed = 'move';
          event.stopPropagation();
        }}
        onDragOver={(event) => {
          if (floating || !event.dataTransfer.types.includes(GROUP_DRAG_TYPE)) return;
          event.preventDefault();
          event.stopPropagation();
          event.dataTransfer.dropEffect = 'move';
          const box = event.currentTarget.getBoundingClientRect();
          setGroupDropEdge(event.clientY - box.top < box.height / 2 ? 'above' : 'below');
        }}
        onDragLeave={() => setGroupDropEdge(null)}
        onDragEnd={() => setGroupDropEdge(null)}
        onDrop={(event) => {
          const moved = event.dataTransfer.getData(GROUP_DRAG_TYPE);
          const edge = groupDropEdge ?? 'above';
          setGroupDropEdge(null);
          if (floating || !moved || moved === String(group.id)) return;
          event.preventDefault();
          event.stopPropagation();
          onDropGroup?.(moved as GroupId, edge);
        }}
        style={{ height: collapsed ? GROUP_COLLAPSED_STRIP_HEIGHT : GROUP_TITLE_ROW_HEIGHT }}
      >
        <span
          className="ez-group-header__title-cell"
          role="gridcell"
          aria-colindex={1}
          style={collapsed ? { width: stickyWidth } : undefined}
        >
          {/* P-030: monday paints the 6 px group bar through the collapsed strip too. */}
          {collapsed ? (
            <span
              className="ez-row__bar"
              style={{ width: GROUP_BAR_WIDTH, backgroundColor: group.color }}
              aria-hidden="true"
            />
          ) : null}
          <button
            type="button"
            className="ez-group-header__menu"
            aria-label={`${group.title} options`}
            onClick={(event) => {
              const box = event.currentTarget.getBoundingClientRect();
              onOpenGroupMenu(box.left, box.bottom);
            }}
          >
            <Icon icon={icons.moreActions} size={16} ignoreFocusStyle />
          </button>

          <button
            type="button"
            className="ez-group-header__chevron"
            aria-label={collapsed ? `Expand ${group.title}` : `Collapse ${group.title}`}
            aria-expanded={!collapsed}
            style={{ color: group.color }}
            onClick={onToggleCollapsed}
          >
            <Icon
              icon={collapsed ? icons.disclosureClosed : icons.disclosureOpen}
              size={16}
              ignoreFocusStyle
            />
          </button>

          {editingTitle && !floating ? (
            <input
              className="ez-group-header__input"

              autoFocus
              defaultValue={group.title}
              aria-label={group.title}
              style={{ color: group.color }}
              onKeyDown={(event) => {
                event.stopPropagation();
                if (event.key === 'Enter') {
                  onRenameGroup((event.target as HTMLInputElement).value.trim() || group.title);
                  setEditingTitle(false);
                }
                if (event.key === 'Escape') setEditingTitle(false);
              }}
              onBlur={(event) => {
                onRenameGroup(event.target.value.trim() || group.title);
                setEditingTitle(false);
              }}
            />
          ) : (
            <span
              className="ez-group-header__title"
              /*
               * P-108: the inventory counts by test id, and the **title** is what a reader
               * and a test both aim at for the group's drag handle — the row around it is
               * what carries `draggable` `[B2§5]`.
               */
              data-testid={`group-drag-${group.id}${floating ? '-floating' : ''}`}
              style={{ color: group.color }}
              onDoubleClick={() => setEditingTitle(true)}
            >
              {group.title}
            </span>
          )}

          {floating ? null : (
            <span className="ez-group-header__counters">
              {/*
                * Singular at one, as monday's own header does (`Done 7 Items / 1 Subitem`,
                * `[uis]` §1) — it read `1 items` on every group with a single row. The item
                * word is the board's own terminology, so a board whose items are "Deals"
                * counts `1 Deal`.
                */}
              <span
                data-testid={`group-count-${group.id}`}
              >{`${group.page.total} ${group.page.total === 1 ? itemTerminology : itemTerminologyPlural}`}</span>
              {group.subitemCount > 0 ? (
                <span>{`/ ${group.subitemCount} ${group.subitemCount === 1 ? 'Subitem' : 'Subitems'}`}</span>
              ) : null}
            </span>
          )}
        </span>
        {/*
          * P-030: a collapsed group keeps its columns' summary cells, drawn to the full grid
          * width with the same vertical rules. `left`/`width` come from the same prefix sum
          * the body rows use, so the rules land on monday's x.
          */}
        {collapsed && !floating ? (
          <div className="ez-group-header__summaries" role="presentation" style={{ width: scrollWidth }}>
            {scrolling.slice(columnRange.start, columnRange.end).map((column, offset) => {
              const box = layout[columnRange.start + offset];
              if (!box) return null;
              return (
                <SummaryCell
                  key={column.id}
                  group={group}
                  column={column}
                  labelled
                  style={{ position: 'absolute', left: box.left, width: box.width }}
                  onOpenSummaryMenu={onOpenSummaryMenu}
                />
              );
            })}
          </div>
        ) : null}
      </div>

      {collapsed ? null : (
        <div className="ez-group-header__columns" role="row">
          <div className="ez-group-header__sticky" role="presentation" style={{ width: stickyWidth }}>
            <span
              className="ez-row__bar"
              style={{ width: GROUP_BAR_WIDTH, backgroundColor: group.color }}
              aria-hidden="true"
            />
            <span className="ez-row__gutter" role="columnheader" style={{ width: CHECKBOX_GUTTER }}>
              <input
                type="checkbox"
                className="ez-row__checkbox"
                /* P-108: the group's select-all, addressable like the rows' own checkboxes. */
                data-testid={`group-select-${group.id}${floating ? '-floating' : ''}`}
                checked={allSelected}
                aria-label={
                  allSelected ? `Unselect all items in ${group.title}` : `Select all items in ${group.title}`
                }
                onChange={(event) => onSelectAll(event.target.checked)}
              />
            </span>
            {nameColumn ? (
              <ColumnHeader
                column={nameColumn}
                width={nameWidth}
                /* P-021: a drag writes the sticky region, which is what monday stores. */
                widthOffset={NAME_STICKY_CHROME_WIDTH}
                minWidth={NAME_COLUMN_MIN_WIDTH}
                sticky
                sorted={sortedBy.get(nameColumn.id) ?? null}
                filtered={filteredColumnIds.has(nameColumn.id)}
                onSort={(direction) => onSortColumn(nameColumn.id, direction)}
                draggable={false}
                onRename={(title) => onRenameColumn(nameColumn.id, title)}
                onResize={(width) => onResizeColumn(nameColumn.id, width)}
                onOpenMenu={(x, y) => onOpenColumnMenu(nameColumn.id, x, y)}
                onDropBefore={() => undefined}
              />
            ) : null}
            <span className="ez-group-header__chat" role="columnheader" aria-hidden="true" />
            {extraPinned.map((column) => (
              <ColumnHeader
                key={column.id}
                column={column}
                width={layout.find((entry) => entry.id === column.id)?.width ?? 140}
                sticky
                sorted={sortedBy.get(column.id) ?? null}
                filtered={filteredColumnIds.has(column.id)}
                onSort={(direction) => onSortColumn(column.id, direction)}
                onRename={(title) => onRenameColumn(column.id, title)}
                onResize={(width) => onResizeColumn(column.id, width)}
                onOpenMenu={(x, y) => onOpenColumnMenu(column.id, x, y)}
                nextColumnId={nextColumnIdOf(column.id)}
                /* P-100: the **moved** column is the one on the dataTransfer, not this one. */
                onDropBefore={(moved, before) => onMoveColumn(moved, before)}
              />
            ))}
          </div>

          <div className="ez-group-header__scroll" role="presentation" style={{ width: scrollWidth }}>
            {scrolling.slice(columnRange.start, columnRange.end).map((column, offset) => {
              const box = layout[columnRange.start + offset];
              if (!box) return null;
              return (
                <ColumnHeader
                  key={column.id}
                  column={column}
                  left={box.left}
                  width={box.width}
                  sorted={sortedBy.get(column.id) ?? null}
                  filtered={filteredColumnIds.has(column.id)}
                  onSort={(direction) => onSortColumn(column.id, direction)}
                  onRename={(title) => onRenameColumn(column.id, title)}
                  onResize={(width) => onResizeColumn(column.id, width)}
                  onOpenMenu={(x, y) => onOpenColumnMenu(column.id, x, y)}
                  nextColumnId={nextColumnIdOf(column.id)}
                  /* P-100: the **moved** column is the one on the dataTransfer, not this one. */
                  onDropBefore={(moved, before) => onMoveColumn(moved, before)}
                />
              );
            })}
          </div>

          <button
            type="button"
            className="ez-add-column"
            role="columnheader"
            aria-label="Add column"
            data-testid="add-column"
            onClick={(event) => {
              const box = event.currentTarget.getBoundingClientRect();
              onAddColumn(box.left, box.bottom);
            }}
          >
            <Icon icon={icons.add} size={16} ignoreFocusStyle />
          </button>
        </div>
      )}
    </div>
  );
}
